<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Bell } from '@lucide/vue'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import NotificationItem from '@/features/notifications/components/NotificationItem.vue'
import { useOpenNotification } from '@/features/notifications/composables/useOpenNotification'
import { useDeleteNotification } from '@/features/notifications/mutations/useDeleteNotification'
import { useMarkAllNotificationsRead } from '@/features/notifications/mutations/useMarkAllNotificationsRead'
import { useNotifications } from '@/features/notifications/queries/useNotifications'
import { useUnreadNotificationCount } from '@/features/notifications/queries/useUnreadNotificationCount'

const { t } = useI18n()

const open = ref(false)

const { data: unreadCount } = useUnreadNotificationCount()
const { data: notifications, isPending, isError } = useNotifications(open)
const openNotification = useOpenNotification()
const markAllRead = useMarkAllNotificationsRead()
const deleteNotification = useDeleteNotification()

const badgeLabel = computed(() => {
  const count = unreadCount.value ?? 0
  return count > 9 ? '9+' : String(count)
})
</script>

<template>
  <DropdownMenu v-model:open="open">
    <DropdownMenuTrigger as-child>
      <Button
        variant="ghost"
        size="icon"
        class="hover:bg-primary/10 relative rounded-full aria-expanded:bg-transparent"
        :aria-label="t('navigation.notifications')"
      >
        <Bell class="size-5" />
        <span
          v-if="unreadCount"
          class="bg-primary text-primary-foreground absolute top-0.5 right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold"
        >
          {{ badgeLabel }}
        </span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-96 p-0" align="end" :side-offset="6">
      <div class="flex h-9 items-center justify-between border-b pr-2 pl-3">
        <span class="text-sm font-medium">{{ t('notifications.title') }}</span>
        <button
          v-if="unreadCount"
          type="button"
          class="text-primary text-xs hover:underline disabled:opacity-50"
          :disabled="markAllRead.isPending.value"
          @click="markAllRead.mutate()"
        >
          {{ t('notifications.markAllRead') }}
        </button>
      </div>

      <div v-if="isPending" class="space-y-2 p-3">
        <Skeleton v-for="n in 3" :key="n" class="h-12" />
      </div>
      <p v-else-if="isError" class="text-muted-foreground py-6 text-center text-sm">
        {{ t('notifications.loadFailed') }}
      </p>
      <p v-else-if="!notifications?.length" class="text-muted-foreground py-6 text-center text-sm">
        {{ t('notifications.empty') }}
      </p>
      <div v-else class="max-h-96 overflow-y-auto p-1">
        <NotificationItem
          v-for="notification in notifications"
          :key="notification.id"
          :notification="notification"
          @open="openNotification"
          @delete="deleteNotification.mutate($event.id)"
        />
      </div>

      <div class="flex h-8 items-center justify-between border-t px-3 text-xs">
        <DropdownMenuItem
          as-child
          class="text-primary h-auto p-0 text-xs hover:underline focus:bg-transparent focus:text-primary"
        >
          <RouterLink to="/notifications">{{ t('notifications.viewAll') }}</RouterLink>
        </DropdownMenuItem>
        <DropdownMenuItem
          as-child
          class="text-muted-foreground h-auto p-0 text-xs underline focus:bg-transparent hover:text-foreground"
        >
          <RouterLink to="/profile">{{ t('notifications.settings') }}</RouterLink>
        </DropdownMenuItem>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
